import { useCallback, useEffect, useState, type FormEvent } from 'react';
import Modal from '../common/Modal';
import { createHankoClient } from '../../utils/hanko';
import { reportPasswordChanged } from '../../api/auth';

// Hanko's own rules: password.min_length in portal-api/hanko/config.yaml, and the
// bcrypt cap Hanko enforces as MaxLength(72). Checked here first so the common
// mistakes get a clear message; Hanko still has the final say.
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 72;

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

type ActionName = 'password_update' | 'password_create';

interface FlowError {
  code: string;
  message: string;
}

// The parts of the Hanko SDK's profile-flow State/Action objects we use. The SDK
// returns a disabled stand-in (not undefined) for an action a state doesn't offer,
// so check `enabled`, never truthiness.
interface PasswordAction {
  enabled: boolean;
  inputs?: { password?: { error?: FlowError } };
  run: (inputs: { password: string }) => Promise<ProfileState>;
}

interface ProfileState {
  name: string;
  error?: FlowError;
  actions?: Partial<Record<ActionName, PasswordAction>>;
}

type Phase = 'loading' | 'blocked' | 'form' | 'saving' | 'done';

const SESSION_EXPIRED =
  'Your session has expired. Please sign out and sign in again to change your password.';
const UNAVAILABLE = 'Password sign-in is not available for this account.';
const LOAD_FAILED = 'Could not load your password settings. Please try again.';
const CHANGE_FAILED = 'Could not change your password. Please try again.';

const ignoreClose = () => {};

function offeredAction(state: ProfileState): ActionName | null {
  if (state.actions?.password_update?.enabled) return 'password_update';
  if (state.actions?.password_create?.enabled) return 'password_create';
  return null;
}

function checkPasswords(password: string, confirm: string): string | null {
  if (password.length < PASSWORD_MIN_LENGTH) return `Use at least ${PASSWORD_MIN_LENGTH} characters.`;
  if (password.length > PASSWORD_MAX_LENGTH) return `Use ${PASSWORD_MAX_LENGTH} characters or fewer.`;
  // Hanko (Go len) counts the 72 in UTF-8 bytes, so accented letters and emoji use more.
  if (new TextEncoder().encode(password).length > PASSWORD_MAX_LENGTH) {
    return 'That password is too long. Some letters and symbols count as more than one character.';
  }
  if (password !== confirm) return 'The passwords do not match.';
  return null;
}

/**
 * Change (or, for a passkey-only user, set) the sign-in password through the user's
 * own Hanko session, then tell portal-api so it records the change and emails a notice.
 * Hanko calls use the logged-in session, never the impersonated user, so Settings
 * keeps this closed while an SA is impersonating.
 * Design: plans/cloudunifi-portal/2026-11-02-change-password-design.md
 */
export default function ChangePasswordModal({ isOpen, onClose }: Props) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [blockedMessage, setBlockedMessage] = useState('');
  const [profile, setProfile] = useState<ProfileState | null>(null);
  const [actionName, setActionName] = useState<ActionName | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Never clears `error`, so a failure message survives the reload that follows it.
  const applyState = useCallback((state: ProfileState) => {
    if (state.name !== 'profile_init') {
      setBlockedMessage(SESSION_EXPIRED);
      setPhase('blocked');
      return;
    }
    const name = offeredAction(state);
    if (!name) {
      setBlockedMessage(UNAVAILABLE);
      setPhase('blocked');
      return;
    }
    setProfile(state);
    setActionName(name);
    setPhase('form');
  }, []);

  const loadProfile = useCallback(async () => {
    try {
      const state = (await createHankoClient().createState('profile', {
        loadFromCache: false,
      })) as unknown as ProfileState;
      applyState(state);
    } catch (err) {
      console.error('Failed to load profile:', err);
      setBlockedMessage(LOAD_FAILED);
      setPhase('blocked');
    }
  }, [applyState]);

  useEffect(() => {
    if (isOpen) {
      // Microtask defer keeps every setState in loadProfile off the effect's
      // synchronous path (react-hooks/set-state-in-effect).
      void Promise.resolve().then(loadProfile);
    }
  }, [isOpen, loadProfile]);

  const handleClose = useCallback(() => {
    setPhase('loading');
    setProfile(null);
    setActionName(null);
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    onClose();
  }, [onClose]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const action = actionName ? profile?.actions?.[actionName] : undefined;
    if (!actionName || !action) return;

    const problem = checkPasswords(newPassword, confirmPassword);
    if (problem) {
      setError(problem);
      return;
    }

    setError(null);
    setPhase('saving');
    try {
      const next = await action.run({ password: newPassword });
      const inputError = next.actions?.[actionName]?.inputs?.password?.error;
      if (next.name === 'profile_init' && !next.error && !inputError) {
        setNewPassword('');
        setConfirmPassword('');
        setPhase('done');
        // Best-effort: the password HAS changed, whatever portal-api says.
        reportPasswordChanged(actionName === 'password_create' ? 'created' : 'updated').catch((err) =>
          console.error('Could not record the password change:', err)
        );
        return;
      }
      setError(inputError?.message ?? next.error?.message ?? CHANGE_FAILED);
      // An SDK state runs one action only: retry on the state Hanko returned.
      if (next.name === 'profile_init') applyState(next);
      else await loadProfile();
    } catch (err) {
      console.error('Failed to change password:', err);
      setError(CHANGE_FAILED);
      await loadProfile();
    }
  };

  const isCreate = actionName === 'password_create';
  const saving = phase === 'saving';

  // No closing mid-save (X, Escape, backdrop, Cancel): the customer must see whether
  // the change happened, and a late result must not leak into the next open.
  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? ignoreClose : handleClose}
      title={isCreate ? 'Set a password' : 'Change password'}
    >
      {phase === 'loading' && (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      )}

      {phase === 'blocked' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">{blockedMessage}</p>
          <button
            type="button"
            onClick={handleClose}
            className="w-full px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md font-medium transition-colors"
          >
            Close
          </button>
        </div>
      )}

      {(phase === 'form' || saving) && (
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {isCreate && (
            <p className="text-sm text-gray-600">
              You sign in with a passkey. A password gives you a second way to sign in.
            </p>
          )}

          <div>
            <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
              New password
            </label>
            <input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={saving}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">At least {PASSWORD_MIN_LENGTH} characters.</p>
          </div>

          <div>
            <label htmlFor="confirm-new-password" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm new password
            </label>
            <input
              id="confirm-new-password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={saving}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={saving}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-md font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed rounded-md font-medium transition-colors"
            >
              {saving ? 'Saving...' : 'Save password'}
            </button>
          </div>
        </form>
      )}

      {phase === 'done' && (
        <div className="flex flex-col items-center justify-center py-6">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-gray-900 font-medium mb-4">
            {isCreate ? 'Your password has been set.' : 'Your password has been changed.'}
          </p>
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md font-medium transition-colors"
          >
            Close
          </button>
        </div>
      )}
    </Modal>
  );
}
